import React, { useEffect, useState } from "react"
import { Typography, Button, useMediaQuery } from "@mui/material"
import { motion, AnimatePresence } from "framer-motion"
import { useDemo } from "../context/DemoContext"

const STORAGE_KEY = "desktop_overlay_dismissed"

const DesktopOnlyOverlay = ({ onContinue }) => {
  const isMobile = useMediaQuery("(max-width:768px)")
  const { hasDemoAccess, setFeedbackTimerStarted } = useDemo()

  const [dismissed, setDismissed] = useState(true) // start hidden until we check

  useEffect(() => {
    if (!hasDemoAccess) return

    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) {
      setDismissed(false) // show only if never dismissed
    }
  }, [hasDemoAccess])

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, "true")
    setDismissed(true)
    setTimeout(() => {
      setFeedbackTimerStarted(true)
    }, 400)
  }

  // 👇 Gate conditions
  if (!isMobile || dismissed || !hasDemoAccess) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 99999,
          backdropFilter: "blur(18px)",
          background: "rgba(10,10,20,0.75)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <motion.div
          initial={{ scale: 0.9, y: 40, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          style={{
            width: "90%",
            maxWidth: 420,
            borderRadius: 20,
            padding: "30px 24px",
            background: "linear-gradient(135deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02))",
            border: "1px solid rgba(255,255,255,0.15)",
            boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
            textAlign: "center",
            position: "relative",
          }}
        >
          {/* Glow */}
          <div
            style={{
              width: 80,
              height: 80,
              margin: "0 auto 20px",
              borderRadius: "50%",
              background: "radial-gradient(circle, #6C63FF, transparent 70%)",
              filter: "blur(20px)",
              position: "absolute",
              top: -20,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: -1,
            }}
          />

          {/* Floating Icon */}
          <motion.div animate={{ y: [0, -6, 0] }} transition={{ repeat: Infinity, duration: 2 }} style={{ fontSize: 40, marginBottom: 10 }}>
            💻
          </motion.div>

          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "#fff",
              mb: 1,
            }}
          >
            Optimized for Desktop
          </Typography>

          <Typography
            sx={{
              color: "rgba(255,255,255,0.7)",
              fontSize: 14,
              mb: 3,
              lineHeight: 1.6,
            }}
          >
            This preview is crafted for a full desktop experience with rich analytics, interactions, and performance insights.
          </Typography>

          <Button
            fullWidth
            onClick={handleDismiss}
            sx={{
              mb: 1.5,
              py: 1.3,
              fontWeight: 700,
              borderRadius: "999px",
              background: "linear-gradient(135deg, #6C63FF, #5A54E8)",
              color: "#fff",
              boxShadow: "0 10px 30px rgba(108,99,255,0.4)",
              "&:hover": {
                transform: "translateY(-2px)",
              },
            }}
          >
            Continue Anyway
          </Button>

          <Typography
            sx={{
              fontSize: 12,
              color: "rgba(255,255,255,0.5)",
            }}
          >
            For best results, open on laptop or desktop
          </Typography>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

export default DesktopOnlyOverlay
